// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadAccountTiles from "@pacific/App/Handlers/UserQuickLinks/LoadAccountTiles";
import { AccountTilesModel } from "@pacific/App/Services/AccountService";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { xHasRoles } from "@pacific/App/Store/Selectors/SessionSelectors";
import { USER_ROLES } from "@pacific/XDL/constants";
import CaretRight from "@pacific/XDL/Icons/CaretRight";
import { fontConfig, headingsFontConfig } from "@pacific/XDL/PreStyle/typography";
import { xBlueprintName } from "@pacific/XDL/types";
import { color, layoutContainer, xScreen } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    itemsList = "itemsList",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
}

interface OwnProps {
    fields: {
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.itemsList]: {
            fields: {
                title: string;
                imageUrl: string;
                linkTitle: string;
                link: LinkFieldValue;
                type:
                    | "openOrdersCount"
                    | "openQuotesCount"
                    | "openInvoicesCount"
                    | "openCreditsCount"
                    | "productListsCount";
                gaEvent: string;
            };
        }[];
    };
}

const mapDispatchToProps = {
    loadAccountTiles,
};

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const showSaleRep = xHasRoles(state, [USER_ROLES.BUYER1, USER_ROLES.BUYER2]);
    const requisitionerRole = xHasRoles(state, [USER_ROLES.REQUISITIONER]);

    return {
        myName: `${session.firstName || ""} ${session.lastName || ""}`,
        showSaleRep,
        salesperson: (session as any).salesperson,
        requisitionerRole,
        isAuthenticated: xIsAuthenticated(state),
        // XNG-Change
        currentBillTo: getCurrentBillToState(state).value,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & WidgetProps;

interface UserQuickLinksStyles {
    container?: InjectableCss;
    wrapper?: GridContainerProps;
    infoSection?: GridItemProps;
    titleSection?: InjectableCss;
    itemsSection?: GridItemProps;
    title?: TypographyPresentationProps;
    subtitle?: TypographyPresentationProps;
    blockContent?: InjectableCss;
    item?: {
        number?: TypographyPresentationProps;
        wrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        link?: LinkPresentationProps;
    };
}

const UserQuickLinksStyles: UserQuickLinksStyles = {
    container: {
        css: css`
            background-color: ${color("common.ui14")};
            padding: 20px 0;
        `,
    },
    wrapper: {
        gap: 20,
        css: css`
            ${layoutContainer};
        `,
    },
    infoSection: {
        width: [12, 12, 3, 2, 2],
        css: css`
            flex-direction: column;
        `,
    },
    itemsSection: {
        css: css`
            column-gap: 20px;
            row-gap: 20px;
            flex-wrap: wrap;
        `,
    },
    titleSection: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    title: {
        size: 12,
        lineHeight: "18px",
        weight: fontConfig.weight.medium,
    },
    subtitle: {
        size: 15,
        lineHeight: "21px",
        weight: fontConfig.weight.semiBold,
    },
    blockContent: {
        css: css`
            background-color: ${color("common.white")};
            border: 1px solid ${color("common.border")};
            flex: 1 0 242px;
            display: flex;
            flex-wrap: nowrap;
            overflow: hidden;
            height: 74px;
            width: 100%;

            ${xScreen("lte", "md")} {
                width: calc(50% - 20px);
                flex: 1 0 calc(50% - 20px);
            }

            ${xScreen("lt", "sm")} {
                width: 100%;
                flex: 1 0 100%;
            }
        `,
    },
    item: {
        number: {
            size: 28,
            fontFamily: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily,
            weight: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].weight.bold,
            lineHeight: "32px",
            css: css`
                background-color: ${color("common.ui2")};
                border-right: 1px solid ${color("common.border")};
                text-align: center;
                height: 74px;
                min-width: 74px;
                padding: 22px 12px;
            `,
        },
        wrapper: {
            css: css`
                padding: 12px;
                display: flex;
                flex-direction: column;
            `,
        },
        title: {
            variant: "h4",
            css: css`
                margin-bottom: 0;
            `,
        },
        link: {
            color: "text.link",
            hoverStyle: {
                color: "common.ui8",
            },
            icon: {
                iconProps: {
                    src: CaretRight,
                    size: 8,
                    color: "text.link",
                },
                position: "right",
            },
            typographyProps: {
                size: 12,
                weight: 600,
                css: css`
                    letter-spacing: 0;
                    line-height: 15px;
                `,
            },
            css: css`
                justify-content: flex-end;
                margin-top: 8px;
                ${IconWrapper} {
                    margin-left: 4px;
                }
                &:focus {
                    outline: none;
                }
                align-items: baseline;
            `,
        },
    },
};

const styles = UserQuickLinksStyles;

const UserQuickLinks: React.FC<Props> = ({
    fields,
    myName,
    showSaleRep,
    salesperson,
    requisitionerRole,
    isAuthenticated,
    loadAccountTiles,
    currentBillTo,
}) => {
    const [data, setData] = React.useState<AccountTilesModel>();
    const buttonLink = useGetLink(fields.buttonLink);
    const itemsLinks = useGetLinks(fields.itemsList ?? [], o => o.fields.link);

    if (!isAuthenticated) {
        return null;
    }

    React.useEffect(() => {
        loadAccountTiles({
            onSuccess: result => {
                setData(result);
            },
            onError: error => {},
        });
    }, []);

    if (!data) {
        return null;
    }

    const _GaHeaderWidgetEvents = (event?: string, title?: string, url?: string) => {
        const dataLayer = getDataLayer();
        if (!dataLayer || !event) {
            return;
        }
        const googleAnalyticsData = {
            event,
            click_text: title,
            destination: url,
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: salesperson?.name || "",
        };
        dataLayer.push(googleAnalyticsData);
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="UserQuickLinks">
            <GridContainer {...styles.wrapper}>
                <GridItem {...styles.infoSection}>
                    <StyledWrapper {...styles.titleSection}>
                        <Typography {...styles.title} data-test-selector="UserQuickLinks_title">
                            {translate("Welcome Back")}
                        </Typography>
                        <Typography {...styles.subtitle} data-test-selector="UserQuickLinks_subtitle">
                            {myName}
                        </Typography>
                    </StyledWrapper>
                    <Link href={buttonLink?.url} {...styles.item?.link} aria-label={fields.buttonLabel}>
                        {fields.buttonLabel}
                    </Link>
                </GridItem>
                {showSaleRep && (
                    <GridItem {...styles.infoSection}>
                        <StyledWrapper {...styles.titleSection}>
                            <Typography {...styles.title} data-test-selector="UserQuickLinks_title">
                                {translate("My Rep")}
                            </Typography>
                            <Typography {...styles.subtitle} data-test-selector="UserQuickLinks_subtitle">
                                {salesperson.name}
                            </Typography>
                        </StyledWrapper>
                        {salesperson.phone && (
                            <Link
                                href={`tel:${salesperson.phone}`}
                                {...styles.item?.link}
                                icon={undefined}
                                aria-label={salesperson.phone}
                            >
                                {salesperson.phone}
                            </Link>
                        )}
                        {salesperson.email && (
                            <Link
                                href={`mailto:${salesperson.email}`}
                                {...styles.item?.link}
                                icon={undefined}
                                aria-label={salesperson.email}
                            >
                                {salesperson.email}
                            </Link>
                        )}
                    </GridItem>
                )}
                <GridItem {...styles.itemsSection} width={showSaleRep ? [12, 12, 6, 8, 8] : [12, 12, 9, 10, 10]}>
                    {fields.itemsList?.map((itemField, index) => {
                        if (
                            data[itemField.fields.type] === -1 ||
                            (requisitionerRole && itemField.fields.type !== "productListsCount")
                        ) {
                            return;
                        }
                        const item = {
                            title: itemField.fields.title,
                            numOfAvailable: data[itemField.fields.type] || 0,
                            linkTitle: itemField.fields.linkTitle,
                            link: itemsLinks?.[index].url || "",
                            gaEvent: itemField.fields.gaEvent,
                        };

                        return (
                            <StyledWrapper {...styles.blockContent} key={`${item.title}_${index}`}>
                                <Typography {...styles.item?.number}>{item.numOfAvailable}</Typography>
                                <StyledWrapper {...styles.item?.wrapper}>
                                    <Typography {...styles.item?.title}>{item.title}</Typography>
                                    <Link
                                        href={item.link}
                                        {...styles.item?.link}
                                        aria-label={item.linkTitle}
                                        onClick={() =>
                                            _GaHeaderWidgetEvents(item.gaEvent, item.linkTitle, item.link)
                                        }
                                    >
                                        {item.linkTitle}
                                    </Link>
                                </StyledWrapper>
                            </StyledWrapper>
                        );
                    })}
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(UserQuickLinks),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                fieldType: "General",
                defaultValue: "View My Dashboard",
            },
            {
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: "itemsList",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => !item.fields.isAuthenticated,
                fieldDefinitions: [
                    {
                        name: "type",
                        displayName: "Type",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Open Orders", value: "openOrdersCount" },
                            { displayName: "My Invoices", value: "openInvoicesCount" },
                            { displayName: "My Quotes", value: "openQuotesCount" },
                            { displayName: "Open Credits", value: "openCreditsCount" },
                            { displayName: "Product Lists", value: "productListsCount" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "openOrdersCount",
                    },
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "linkTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                        allowCategories: item => false,
                    },
                    {
                        name: "gaEvent",
                        displayName: "GA Event Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
